import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Observable, Subject, filter, map, of, from, catchError } from 'rxjs';

import { LoggingService } from '../../logging/logging.service';

import { CacheEventBus } from './cache-event-bus.service';
import { CacheManagerService } from './cache-manager.service';
import { CacheRevalidationService } from './cache-revalidation.service';
/**
 * Service for handling service worker updates and cache invalidation.
 * This service should be initialized in the root application component.
 */
@Injectable({
  providedIn: 'root'
})
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly logger = inject(LoggingService);
  private readonly cacheEventBus = inject(CacheEventBus);
  private readonly cacheManager = inject(CacheManagerService);
  private readonly revalidationService = inject(CacheRevalidationService);

  // Update check interval in milliseconds
  private readonly UPDATE_CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes

  // Subject for update availability notifications
  private updateAvailable$ = new Subject<{ version: string, hash: string }>();

  /**
   * Initialize the service worker update handling.
   * Should be called once in the app's initialization.
   *
   * @param projectId The project ID to scope of the cache revalidation.
   * @param promptForUpdate Whether to prompt the user to update when a new version is available.
   * @param autoCheck Whether to periodically check for updates.
   *
   * @returns Observable that emits when initialization is complete.
   */
  public initialize(projectId: string, promptForUpdate = false, autoCheck = true): Observable<boolean> {
    if (!this.swUpdate || !this.swUpdate.isEnabled) {
      this.logger.debug('Service worker updates are not enabled');
      return of(false);
    }

    // Listen for available updates
    this.swUpdate.versionUpdates.pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY')
    ).subscribe(event => {
      const versionInfo = {
        version: event.latestVersion.appData && 'version' in event.latestVersion.appData
          ? String(event.latestVersion.appData.version)
          : 'unknown',
        hash: event.latestVersion.hash
      };

      this.logger.info(`New app version available: ${versionInfo.hash}`);

      // Notify listeners about the update
      this.updateAvailable$.next(versionInfo);

      // Dispatch a cache invalidation even
      this.cacheEventBus.dispatch({
        type: 'clear',
        storeId: '*',
        timestamp: Date.now(),
        source: 'serviceWorker'
      });

      // Clear all caches
      this.cacheManager.clearAll().subscribe({
        next: () => {
          this.logger.info('All caches cleared due to application update');

          // Revalidate all caches to ensure fresh data
          this.revalidationService.revalidateAll(projectId).subscribe(() => {
            this.logger.info('All caches revalidated');
          });

          // Prompt user to reload if enabled
          if (promptForUpdate) {
            if (confirm('A new version of the application is available. Load the new version?')) {
              window.location.reload();
            }
          }
        },
        error: (err) => this.logger.error('Error clearing caches:', err)
      });
    });

    // Set up periodic update checks if enabled
    if (autoCheck) {
      setInterval(() => {
        this.checkForUpdate();
      }, this.UPDATE_CHECK_INTERVAL);

      // Initial check
      this.checkForUpdate();
    }

    return of(true);
  }

  /**
   * Manually check for app updates.
   *
   * @returns Observable that emits when check is complete.
   */
  public checkForUpdate(): Observable<boolean> {
    if (!this.swUpdate || !this.swUpdate.isEnabled) {
      return of(false);
    }

    return from(this.swUpdate.checkForUpdate())
      .pipe(
        map(() => true),
        catchError(err => {
          this.logger.error('Error checking for updates:', err);
          return of(false);
        })
      );
  }

  /**
   * Get notifications when updates are available.
   *
   * @returns Observable that emits when a new version is available.
   */
  public watchForUpdates(): Observable<{ version: string, hash: string }> {
    return this.updateAvailable$.asObservable();
  }

  /**
   * Activate latest version (if available) and reload the app.
   *
   * @returns Promise that resolves when activation completes.
   */
  public async activateUpdate(): Promise<boolean> {
    if (!this.swUpdate || !this.swUpdate.isEnabled) {
      return false;
    }

    try {
      await this.swUpdate.activateUpdate();
      this.logger.debug('Update activated successfully');
      window.location.reload();
      return true;
    } catch (err) {
      this.logger.error('Error activating update:', err);
      return false;
    }
  }
}
